import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, Overline } from "@/components/Primitives";
import { CONTACT, SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "rounded-none border-0 border-b border-border bg-transparent px-0 py-6 text-lg text-obsidian shadow-none placeholder:text-stone/50 focus-visible:border-obsidian focus-visible:ring-0 focus-visible:ring-offset-0";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ full_name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-obsidian text-bone">
        <div className="mx-auto max-w-[1400px] px-6 pb-16 pt-40 lg:px-12 lg:pb-20 lg:pt-52">
          <Reveal>
            <Overline dark>Contact</Overline>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight md:text-7xl">
              Start the <span className="italic text-bone/70">conversation.</span>
            </h1>
            <p className="mt-8 max-w-xl text-bone/70 md:text-lg">
              Tell us about your accounting, reporting, or advisory needs and we
              will follow up to discuss the right next step.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-12 lg:py-28">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          {/* Details */}
          <Reveal className="md:col-span-4">
            <div className="space-y-10">
              <div>
                <Overline>Direct</Overline>
                <ul className="mt-6 space-y-6">
                  <li className="flex items-start gap-4">
                    <Phone size={18} className="mt-1 text-brand" />
                    <span data-testid="contact-phone" className="text-obsidian">{CONTACT.phone}</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <Mail size={18} className="mt-1 text-brand" />
                    <span data-testid="contact-email" className="break-all text-obsidian">{CONTACT.email}</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <Clock size={18} className="mt-1 text-brand" />
                    <span className="text-stone">{CONTACT.availability}</span>
                  </li>
                </ul>
              </div>
              <div className="border-t border-border pt-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone">Availability</p>
                <p className="mt-4 font-serif text-2xl text-obsidian">US time zones · EST–PST</p>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="md:col-span-8 md:pl-10">
            {done ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex h-full flex-col items-start justify-center border border-border bg-sage p-12"
              >
                <span className="flex h-14 w-14 items-center justify-center bg-brand text-bone">
                  <Check size={24} />
                </span>
                <h2 className="mt-8 font-serif text-4xl tracking-tight text-obsidian">Thank you.</h2>
                <p className="mt-4 max-w-md text-stone">
                  Your consultation request has been received. A senior advisor will
                  reach out shortly to discuss next steps.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 text-sm font-medium text-brand link-underline"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <form data-testid="consultation-form" onSubmit={handleSubmit} className="space-y-10">
                <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="full_name" className="font-mono text-xs uppercase tracking-[0.15em] text-stone">Full Name *</Label>
                    <Input id="full_name" data-testid="input-full-name" value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" className={fieldClass} />
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-[0.15em] text-stone">Email Address *</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} placeholder="jane@company.com" className={fieldClass} />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-[0.15em] text-stone">Phone Number</Label>
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="+1 (555) 000-0000" className={fieldClass} />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-[0.15em] text-stone">How can we help?</Label>
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger data-testid="select-service" className="mt-0 h-auto rounded-none border-0 border-b border-border bg-transparent px-0 py-6 text-lg text-obsidian shadow-none focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-stone/50">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-border bg-bone">
                        {SERVICES.map((s) => (
                          <SelectItem key={s.id} value={s.title} data-testid={`service-option-${s.id}`} className="rounded-none">
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="font-mono text-xs uppercase tracking-[0.15em] text-stone">Tell us more</Label>
                  <Textarea id="message" data-testid="input-message" value={form.message} onChange={update("message")} placeholder="A few sentences about your needs…" rows={4} className="mt-0 resize-none rounded-none border-0 border-b border-border bg-transparent px-0 py-6 text-lg text-obsidian shadow-none placeholder:text-stone/50 focus-visible:border-obsidian focus-visible:ring-0 focus-visible:ring-offset-0" />
                </div>

                <button
                  type="submit"
                  data-testid="consultation-submit-btn"
                  disabled={submitting}
                  className="group inline-flex items-center gap-2 bg-obsidian px-10 py-5 text-sm font-medium text-bone transition-colors duration-300 hover:bg-brand disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Request Consultation"}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
